/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    ALLBROADCASTS_SIDEBAR_FILTER
 * Date:         2026-01-01
 * Modified By:  Cursor
 *
 * Description:
 * Helper utilities for all broadcasts list - flatten Broadcast[] to FlattenedBroadcastItem[]
 * for use with BroadcastFilters sidebar component (matches Search.tsx pattern).
 * Added pagination utilities.
 *
 * File Size Check:
 * - Current: 129 lines
 * - Adding: ~20 lines
 * - Projected: 149 lines
 * - Decision: Proceed (within 150-line utility limit)
 *
 * Related Screens / Modules:
 * - client/src/features/broadcast/pages/broadcasts-list/utils/broadcastHelpers.ts
 * - client/src/features/broadcast/pages/broadcasts-list/components/BroadcastsTabs.tsx
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    BROADCASTS_FILTER_INTEGRATION
 * Date:         2026-01-01
 * Modified By:  Cursor
 *
 * Description:
 * Helper utilities for broadcasts list page - flatten Broadcast[] to FlattenedBroadcastItem[]
 * for use with BroadcastFilters component.
 *
 * File Size Check:
 * - New file
 * - Projected: ~100 lines
 * - Decision: Proceed (within 150-line utility limit)
 *
 * Related Screens / Modules:
 * - client/src/features/broadcast/pages/broadcasts-list/utils/broadcastHelpers.ts (NEW)
 * - client/src/features/broadcast/pages/broadcasts-list/components/BroadcastsTabs.tsx
 */

import { Broadcast } from "../../../../../interfaces/broadcast";
import { FlattenedBroadcastItem } from "../../../../../components/broadcast/BroadcastItemCard";

// [2026-01-01] Flatten broadcast groups into individual items for filtering
export const flattenBroadcasts = (broadcasts: Broadcast[]): FlattenedBroadcastItem[] => {
  const flattened: FlattenedBroadcastItem[] = [];

  broadcasts.forEach((broadcast) => {
    const broadcastGroup = {
      _id: broadcast._id,
      userId: broadcast.userId,
      type: broadcast.type,
      createdAt: broadcast.createdAt,
      updatedAt: broadcast.updatedAt,
    };

    // Use items array if available, otherwise check broadcast property (compatibility)
    const items = broadcast.items || broadcast.broadcast || [];

    // [2026-07-07] CRASH FIX: `typeof null === "object"` is true — an orphaned broadcast
    // (owner deleted → userId populates to null) would hit null.companyName and white-screen
    // the whole page. Guard with `&& broadcast.userId` (matches the search helper).
    const owner =
      broadcast.userId && typeof broadcast.userId === "object"
        ? (broadcast.userId as any)
        : null;
    items.forEach((item: any) => {
      flattened.push({
        ...item,
        broadcastGroup,
        companyName: owner ? owner.companyName || "" : "",
        userCountry: owner ? owner.country : undefined,
        // [2026-02-10] BUGFIX: Preserve original item.itemNo from database, don't overwrite with array index
      });
    });
  });

  return flattened;
};

// [2026-01-01] Filter flattened broadcasts based on filter criteria
export const filterFlattenedBroadcasts = (
  items: FlattenedBroadcastItem[],
  filters: {
    keyword?: string;
    sku?: string;
    type?: string;
    manufacturer?: string;
    category?: string;
    qty?: number;
    condition?: string;
    country?: string;
    company?: string;
  }
): FlattenedBroadcastItem[] => {
  return items.filter((item) => {
    // Keyword filter
    if (filters.keyword && filters.keyword.trim()) {
      const keyword = filters.keyword.trim().toLowerCase();
      const matchesKeyword =
        (item.title && item.title.toLowerCase().includes(keyword)) ||
        (item.manufacturer && item.manufacturer.toLowerCase().includes(keyword)) ||
        (item.model && item.model.toLowerCase().includes(keyword)) ||
        (item.partNumber && item.partNumber.toLowerCase().includes(keyword)) ||
        (item.description && item.description.toLowerCase().includes(keyword));
      if (!matchesKeyword) return false;
    }

    // SKU filter
    if (filters.sku) {
      const matchesSKU =
        (item.partNumber && item.partNumber.trim() === filters.sku) ||
        (item.model && item.model.trim() === filters.sku);
      if (!matchesSKU) return false;
    }

    // Type filter (WTS/WTB)
    if (filters.type && item.broadcastGroup.type !== filters.type) {
      return false;
    }

    // Manufacturer filter
    if (filters.manufacturer && item.manufacturer !== filters.manufacturer) {
      return false;
    }

    // Category filter
    if (filters.category && (!item.categories || !item.categories.includes(filters.category))) {
      return false;
    }

    // Quantity filter
    if (filters.qty && filters.qty > 0 && item.quantity !== filters.qty) {
      return false;
    }

    // Condition filter
    if (filters.condition && item.condition !== filters.condition) {
      return false;
    }

    // Country filter
    if (filters.country && item.userCountry !== filters.country) {
      return false;
    }

    // Company filter
    if (filters.company && item.companyName !== filters.company) {
      return false;
    }

    return true;
  });
};

// [2026-01-01] Apply pagination to filtered results
export const paginateBroadcasts = (
  items: FlattenedBroadcastItem[],
  currentPage: number,
  itemsPerPage: number
): FlattenedBroadcastItem[] => {
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  return items.slice(startIndex, endIndex);
};

// [2026-01-01] Calculate total pages
export const calculateTotalPages = (totalItems: number, itemsPerPage: number): number => {
  return Math.ceil(totalItems / itemsPerPage);
};
